// object-fall-damage.ts — I1 H3 / R2: the object-fall pieces that are NOT Lane A's damage roll (lane L2).
//
// A falling object's damage is decided in the browser by LANE A's damageTypeRoll() (janus-engine/inventory/object-fall-janus.ts,
// R2-2, 2026-09-28). H3's bespoke resolver (resolveObjectFall: rsort, exploding dice, the composition roll-up, the six defence
// passes, object_damage_mitigation, roll_feedback) is RETIRED — Lane A's path is byte-identical on every case it answered
// (tools/test/janus-h3-object-fall.test.mjs §8 against tools/probes/r2_object_fall_janus_oracle.php). What stays here:
//   · the fall preamble (fallDice / fallTier — apply_fall_damage_to_inventory_in_feet(): negate, the > 5 ft / v_max / ≥ 10 ft gates);
//   · H3's object gate (objectFallUnmodelled) — R1b's container splash still judges each spilled child with it — and the by-id
//     status list the Lane A route's gate shares (DAMAGE_BRANCH_STATUS_IDS);
//   · apply_damage_to_target() on an object for an already-summed total (applyObjectDamageTotal) and the splash total — R1b's spill.

type Row = Record<string, unknown>;

export const FALL_ABILITY = { id: 'BE42824F-46BE-EC80-AB7F-B823190AB6C8', name: 'Take Fall Damage', itemType: 'free_ability' } as const;
export const BLUDGEONING = { id: '0C528CF8-1B5A-453C-9BDE-73CBBC239A2A', name: 'Bludgeoning' } as const;
/** RollType 'damage_roll' — the row's roll_type_1. */
export const DAMAGE_ROLL_TYPE = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
const DIES = [6, 8, 10, 12, 20];

/** The dice a fall of `effectiveFt` rolls (below 10 ft: none). */
export function fallDice(effectiveFt: number): { dice: number; die: number } | null {
  if (!(effectiveFt >= 10)) return null;
  const dice = Math.floor(effectiveFt / 10);
  return { dice, die: DIES[Math.min(dice - 1, 4)]! };
}

/** apply_fall_damage_to_inventory_in_feet()'s preamble: negate ('inf' = none), the > 5 ft / v_max / ≥ 10 ft gates, the dice. */
export function fallTier(input: { fallFt: number; negate: number | 'inf'; vMax: number }):
  { verdict: 'none'; reason: 'negated' | 'short' | 'no-vitality' } | { verdict: 'roll'; dice: number; die: number } {
  if (input.negate === 'inf') return { verdict: 'none', reason: 'negated' };
  const effective = Math.max(0, input.fallFt - Math.max(0, Number(input.negate) || 0));
  if (effective <= 5) return { verdict: 'none', reason: 'short' };
  if (!(Math.trunc(input.vMax) > 0)) return { verdict: 'none', reason: 'no-vitality' };
  const tier = fallDice(effective);
  return tier ? { verdict: 'roll', ...tier } : { verdict: 'none', reason: 'short' };
}

// ── H3 / R2: the object gate (R1b's splash) — which objects H3's model answered EXACTLY ────────────────────────────────────────────────
// Oracle: tools/probes/r2_object_fall_janus_oracle.php re-enacts the real legacy fall (janus() on the object as caster AND
// target, BEGIN … ROLLBACK, taped draws) for every status real inventory rows carry and for one real source of every
// damage-path layer, both authored ON the object and applied TO it as a status. tools/test/janus-h3-object-fall.test.mjs
// holds this gate to it: every case either matches PHP's row byte for byte or hands back here, before any draw.

/** Statuses that carry NO layers and that no damage branch names (h4 oracle status_layers, 2026-09-27). */
export const INERT_STATUS_IDS: ReadonlySet<string> = new Set([
  '36C061BF-E480-4334-961C-26500150E3C4', // Integrated Object
  '477A7977-60D2-4B9C-96B0-6ED4BFD6437E', // Locked
]);
/** Statuses the fall path acts on BY ID, whatever layers they carry (R2-0 trace): stealth_reveal_on_action (hidden / invisible /
 * sneaking), janus_triggers_on_hit (a Mirror Image stack), calc_total (a Shielded stack), apply_damage_to_target_inc.php (sleeping
 * wake, hidden reveal → a target_inventory_revealed row, the charm / domination break, paralyzed, dying / maimed), check_concentration,
 * and the long-cast statuses janus reads. Every stack variant is listed. */
export const DAMAGE_BRANCH_STATUS_IDS: ReadonlySet<string> = new Set([
  '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B', // sleeping
  'E7FDB796-26DA-E0F5-8F88-C21FE01D923B', // concentrating
  'AE967694-B94B-F0FF-B680-829FDE6E8D58', // searching
  '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3', // casting
  '5833A733-D277-0A60-3A94-A0F355A79A80', // casting_ritual_spell
  '1BA5473E-6218-6688-1C68-DCA4FEF00A97', // sneaking
  '47667406-1B72-9D60-310B-D8CF6BFE397D', // hidden
  '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD', // invisible
  'BF31EE0A-5B3C-370B-0118-96B067CFD929', // paralyzed
  'DD4C0995-C3D2-43A3-CFC7-226C3005274C', // charmed
  'D2E7BA2C-562D-D4AC-7F71-13E4AC540484', // dominated
  'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D', // maimed
  '9437F344-1461-B4C0-E196-6BA1A34ADC94', '9154FF1F-721A-2BCA-1AEF-24AC7B32FE4B', '46F27428-2B73-50F7-6F0C-D4FAFDB753ED', // maimed_1…3
  '4D27433B-6F72-47BB-9A60-B80493F22D65', 'E4994653-49B3-D768-35C1-36C8DE40CC1A', '5C557D5A-B85F-DF45-3CF6-6739D2BD70EC', // maimed_4…6
  '44D4504C-9BD5-0B65-F8E2-6DC4A0F820B4', '1DEE12B6-D23A-7470-812E-CE4C77AAD395', '4AEF8F40-B779-C0AD-4455-2DABE7B1D14B', // maimed_7…9
  'A230F5E6-B6AD-8091-4F59-5E454776B4FA', // maimed_10
  'EB6632D3-5EC0-4E4D-5C6C-1BCB7EF57022', // shielded
  'BF823982-6FC8-51F0-F452-5BE1C949FBA6', '36DA5B89-CA08-3940-E798-FCAE6BE775D5', '8346CCFF-3A5B-F0E4-CFDE-6CF1D1ED3368', // shielded_1…3
  'E5314BED-1269-3A65-8C15-01E80A3A8E23', 'E614D75F-CF27-D169-3D91-0D33D9CE354B', 'A831D8E0-4DC5-4FBF-4A4C-2C9955AE230A', // shielded_4…6
  '631AAD91-F6A5-879D-5BD6-B7F885AA7FC9', 'BF1C1C48-8171-90B8-CD83-F2768348629F', '2A63EC91-E5B1-17B4-DFB0-199F9E2084A5', // shielded_7…9
  '01B0F693-7D8A-C588-9B34-CD1F323D8433', // shielded_10
  'CDC5CFDA-B09C-4A68-820D-6C74D90BD595', // mirror_image_effect
  'D9B64F38-A817-47B8-AB07-60D59F3D893C', 'AE20C09B-76BE-5BC4-4E33-6E8CFE1F0139', '479A4B5C-43F3-3559-979B-5BDA8A379F07', // mirror_image_1…3
  '8AE129AF-52D8-CCE8-706A-85767DB52740', // dying
  '78C6CC42-BCCE-4F22-9FC5-41F8D7E70944', 'EE063289-1AF3-41CF-B73C-64A0556E6D07', '2EAAA2E8-1B9C-196D-3E58-C29C09FF8492', // dying_state…_2
  'A9678CB1-11D3-55A3-E3C9-F342B2E40980', // dying_state_3
]);
/** The ONLY layer keys a status on a falling object may carry and still leave the fall exact — an allowlist, so a layer
 * nobody listed hands back. Each is read by no step of the fall (R2-0 trace) and is proven exact by a real status in the
 * oracle: light sources (Create Torchlight, Light Torch / Candle / Campfire, Create Candlelight) and the spell / area
 * statuses a zone leaves on an object (Prismatic Wall, Fog Cloud). */
export const FALL_INERT_STATUS_LAYERS: ReadonlySet<string> = new Set([
  'range', 'duration_int', 'duration_type', 'requires_tool', 'bright_light_dim_light', 'object_destroyed_after_duration_complete',
  'spell_level', 'spell_casting_time', 'spell_component_verbal', 'spell_component_somatic', 'spell_component_material', 'spell_circle',
  'spell_sphere', 'spell_upcastable', 'spell_upcast_increase_aoe_ft', 'aoe_type', 'aoe_distance', 'aoe_distance_depth', 'aoe_distance_height',
  'persistent_aoe', 'effect_only_affects_opposing_team_type',
  // an item-as-status's catalog price (R2-2a: the oracle's full status definitions carry it; no damage step reads it)
  'cost_gp',
]);
/** Layers the fall reads on the object itself that this resolver does NOT model (caster side and the target "against" mirror
 * are the same row). Any one present hands back. */
export const FALL_UNMODELLED_OWN_LAYERS: readonly string[] = [
  // trigger / fragment carriers (fire_caster_makes_roll_type_trigger, janus_triggers_on_hit, damage_type_rolls, the takes-damage fan-out)
  'passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell', 'innate_spell_per_long_rest', 'spells_known',
  'opportunity_attack_ability', 'feat', 'immune_to_ability', 'damage_mitigation', 'ignore_damage_type_resistance', 'ignore_damage_type_immunity', 'attacks_ignore_damage_mitigation',
  'damage_ignores_all_damage_mitigation', // Perfect Breach (2026-10-07): the dealer skips (object) Damage Mitigation — unported for a fall
  'all_damage_output_changed_by_percentage', 'all_incoming_damage_changed_by_percentage', 'damage_taken_pct_mod', 'damage_reduction',
  'damage_type_reroll_1s_and_2s', 'damage_type_roll_1_becomes_2', 'damage_type_roll_adv_dis_stacks', 'damage_type_dice_quantity_mod',
  'reduce_damage_roll', 'transfer_damage_percentage_to_caster_of_status_effect', 'damage_redirect', 'warding_bond',
  'spell_damage_resistance', 'spell_damage_vulnerability', 'extra_damage_taken_roll', 'shapeshifted',
  'roll_type_adv', 'roll_type_dis', 'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min', 'roll_type_roll_result_is_doubled',
  'roll_type_roll_result_is_halved', 'roll_type_against_adv', 'roll_type_against_dis', 'roll_type_against_is_rolled_at_max',
  'roll_type_against_is_rolled_at_min', 'roll_type_against_roll_result_is_doubled', 'roll_type_against_roll_result_is_halved',
];
/** Families no listed key may escape: a new layer in one of them hands back until it is traced. */
const UNMODELLED_OWN_FAMILY = /^(roll_type_|triggers_|extra_damage|ignore_damage|reduce_damage|damage_type_(roll|reroll|dice)|spell_damage_|shapeshift)|incoming_damage|damage_output|damage_taken|transfer_damage|damage_redirect|warding|damage_heals|damage_damages|^damage_mitigation$|^damage_reduction/;

export interface FallGateInput {
  /** Keys of the object's own layers (row + catalog definition) that carry a value. */
  ownLayerKeys: string[];
  /** Each status on the object: its item id and the held definition (null = not held). */
  statuses: Array<{ id: string; definition: { item_layer_types?: unknown } | null }>;
  itemType: string;
  /** A generated wall run (inventory_generation.wallRun): apply_damage_to_target splits the damage per 5 ft section. */
  wallRun: boolean;
  /** inventory_private = 1 and the viewer is neither GM nor employee: validate_and_adjust_targets vetoes the legacy fall
   * (janus_request_explicitly_targets_private_entity — the fall targets the object by id). */
  privateToViewer: boolean;
  /** The row's compiled roll_type_modifiers carry an entry (ADV / DIS on the fragment from any source). */
  rollTypeModifiers: boolean;
  campaignHeld: boolean;
}
/** null = H3's model answers exactly (R1b's splash); otherwise the hand-back reason (decided before any draw). */
export function objectFallUnmodelled(input: FallGateInput): string | null {
  for (const status of input.statuses) {
    const id = String(status.id ?? '').toUpperCase();
    if (INERT_STATUS_IDS.has(id)) continue;
    if (!id || DAMAGE_BRANCH_STATUS_IDS.has(id)) return `status-branch:${id || 'unknown'}`;
    // PHP json-encodes an empty layer map as [].
    const raw = status.definition?.item_layer_types;
    const layers = Array.isArray(raw) && raw.length === 0 ? {} : raw;
    if (!status.definition || (layers !== undefined && (layers === null || typeof layers !== 'object' || Array.isArray(layers))))
      return 'status-definition-not-held';
    const stray = Object.keys((layers ?? {}) as Row).find(key => !FALL_INERT_STATUS_LAYERS.has(key));
    if (stray) return `status-layer:${stray}`;
  }
  const own = input.ownLayerKeys.find(key => FALL_UNMODELLED_OWN_LAYERS.includes(key)
    || (UNMODELLED_OWN_FAMILY.test(key) && key !== 'object_damage_mitigation'));
  if (own) return `layer:${own}`;
  if (input.itemType === 'wall' || input.wallRun) return 'wall-run';
  if (input.privateToViewer) return 'private-object';
  if (input.rollTypeModifiers) return 'roll-type-modifiers';
  if (!input.campaignHeld) return 'campaign-unavailable';
  return null;
}

/**
 * apply_damage_to_target() on an OBJECT for an already-summed total: v_temp absorbs first, then v_current (clamped to
 * [0, effective max]); ft_inventory_forced_destruction_by_damage_type() floors it when one of the fragment's damage types is
 * in destroyed_by_damage_type (a total with no typed fragment — the container splash — never forces it). The fall and R1b's
 * container splash share it. The caller runs objectFallUnmodelled() on the object first (its status / layer gate is a superset
 * of what this step reads).
 */
export function applyObjectDamageTotal(input: { total: number; vCurrent: number; vTemp: number; effectiveVMax: number;
  destroyedByDamageType: string[]; damageTypeIds: string[] }): { vCurrent: number; vTemp: number; destroyed: boolean } {
  let vTemp = Math.max(0, Math.trunc(input.vTemp));
  let damage = input.total;
  if (vTemp > 0) {
    if (vTemp >= damage) { vTemp -= damage; damage = 0; } else { damage -= vTemp; vTemp = 0; }
  }
  let vCurrent = Math.max(0, Math.min(input.effectiveVMax, Math.trunc(input.vCurrent) - damage));
  // apply_damage_to_target_inc.php:291 — the damage LEFT after v_temp (R2-2a fix: was the pre-v_temp total).
  if (damage > 0 && vCurrent > 0 && input.damageTypeIds.some(id => input.destroyedByDamageType.includes(id))) vCurrent = 0;
  return { vCurrent, vTemp, destroyed: vCurrent <= 0 };
}

/**
 * R2-3 — the splash a spilled child takes when its container breaks (release_or_destroy_inventory_after_container_break():
 * max(1, floor(breaking total × 0.35 × multiplier)); inventory_splash_damage_multiplier_for_composition(): 1.75 when the child's
 * effective composition — its first item_composition entry, else its template's — carries fragile_container_splash, else 1).
 * No dice: the total goes straight to applyObjectDamageTotal() with no damage type. For R1b's container spill.
 */
export function splashDamageTotal(breakingDamageTotal: number, fragileComposition: boolean): number {
  return Math.max(1, Math.floor(breakingDamageTotal * 0.35 * (fragileComposition ? 1.75 : 1.0)));
}
